import type { SqlEngine } from '../../../../sql-grammar/model';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { Column, SchemaSnapshot } from '../../../../sql-resolver/types/schema';
import { foldIdentifier } from '../../../../sql-resolver/scope/identifiers';
import { onVisibleItems } from '../../../../sql-resolver/scope/joinOnVisibility';
import {
  querySourceOf,
  resolveSourceRelation,
  sourceColumns,
  type QuerySource,
} from '../../../../sql-resolver/scope/querySources';

export interface InScopeTable {
  /**
   * The table's identity among the listed ones — how the engine tells two
   * FROM-clause names apart (`bindingIdentity`). Unique; the dropdown value.
   */
  readonly key: string;
  /** What the query calls the table — the alias when aliased, else its name. */
  readonly prefix: string;
  /** Display heading for the table <select>. */
  readonly label: string;
  /** The SQL that qualifies its columns, as the query spells it (`oi`, `"order items"`, `[Order Details]`). */
  readonly qualifierSql: string;
  readonly columns: readonly Column[];
}

/**
 * Engines that compare a delimited name exactly and fold a bare one
 * (PostgreSQL lower, Oracle UPPER): `"a"` and `"A"` are two tables there, and
 * a bare `A` names `"a"`. The resolver's quote-aware fold (`bindingKey`) is
 * that identity. SQL Server (collation), MySQL (`lower_case_table_names`) and
 * SQLite (always case-insensitive) have no fold to apply: their names are
 * compared as spelled first, then case-insensitively (`findInScopeTable`).
 */
function foldsBareNames(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

/** A source's identity for `InScopeTable.key` (see `foldsBareNames`). */
function bindingIdentity(source: QuerySource, engine: SqlEngine): string {
  return foldsBareNames(engine) ? (source.bindingKey ?? source.exposedName) : source.exposedName;
}

/**
 * The tables a JOIN's ON predicate can name, for the structured ON editor's
 * table + column dropdowns: the FROM items its ON can see (`onVisibleItems` —
 * the rule a subquery drilled from that ON follows too), each with its
 * snapshot columns (never the schema's other tables, which
 * `scope.reachableTables` also carries). Sources whose columns are unknown
 * (CTEs, derived tables, table functions, tables missing from the snapshot)
 * are left out: the editor then falls back to free text. Two sources the
 * engine tells apart stay two entries (`bindingIdentity`); two whose display
 * name collides (PostgreSQL `"A"` and bare `A`) are labelled by their SQL
 * spelling. Linear in the number of visible items (a name count, not a scan
 * per label): an 800-join statement's ON editor opens at once.
 */
export function listInScopeTables(
  structure: QueryStructure | null | undefined,
  joinIndex: number,
  engine: SqlEngine,
  snapshot: SchemaSnapshot | null | undefined,
): InScopeTable[] {
  if (!structure || !snapshot) return [];
  const seen = new Set<string>();
  const listed: { source: QuerySource; key: string; columns: readonly Column[] }[] = [];
  const nameCount = new Map<string, number>();
  for (const item of onVisibleItems(structure, joinIndex, engine)) {
    const source = resolveSourceRelation(querySourceOf(item, engine), snapshot);
    const columns = sourceColumns(source, snapshot);
    const key = bindingIdentity(source, engine);
    if (columns.length === 0 || seen.has(key)) continue;
    seen.add(key);
    listed.push({ source, key, columns });
    nameCount.set(source.exposedName, (nameCount.get(source.exposedName) ?? 0) + 1);
  }
  return listed.map(({ source, key, columns }) => ({
    key,
    prefix: source.exposedName,
    label: (nameCount.get(source.exposedName) ?? 0) > 1 ? source.qualifierSql : source.exposedName,
    qualifierSql: source.qualifierSql,
    columns,
  }));
}

/**
 * The listed table an ON side's qualifier names. PostgreSQL / Oracle fold the
 * qualifier the way the engine does (`foldIdentifier`: a delimited one exact,
 * a bare one lower / UPPER) and compare it to each table's `key`. The other
 * engines compare the qualifier as spelled first, then case-insensitively —
 * but only when that names exactly one table: a case-insensitive database or
 * SQLite cannot hold two case variants, a case-sensitive one resolves them
 * exactly.
 */
export function findInScopeTable(
  qualifier: { readonly value: string; readonly quoted: boolean },
  tables: readonly InScopeTable[],
  engine: SqlEngine,
): InScopeTable | undefined {
  if (foldsBareNames(engine)) {
    const key = foldIdentifier(qualifier.value, qualifier.quoted, { engine });
    return tables.find((t) => t.key === key);
  }
  const exact = tables.find((t) => t.key === qualifier.value);
  if (exact) return exact;
  const lowered = qualifier.value.toLowerCase();
  const matches = tables.filter((t) => t.key.toLowerCase() === lowered);
  return matches.length === 1 ? matches[0] : undefined;
}
